import type {
  GuideCourse,
  GuideBasicBlock,
  GuideBlock,
  GuideChoice,
  GuideStage,
  GuideEntryPoint,
  GuideImage,
  GuideImageMarker,
  GuideLesson,
  GuideLessonSummary,
} from "../types"

const invalid = (): never => {
  throw new Error("learning_guide_response_invalid")
}
const object = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : invalid()
const text = (value: unknown): string => (typeof value === "string" ? value : invalid())
const optionalText = (value: unknown): string => (value === undefined ? "" : text(value))
const integer = (value: unknown, minimum = 0): number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= minimum ? value : invalid()
const key = (value: unknown): string =>
  typeof value === "string" && /^[a-z][a-z0-9-]*$/.test(value) ? value : invalid()
const array = <T>(value: unknown, parse: (item: unknown) => T): T[] =>
  Array.isArray(value) ? value.map(parse) : invalid()
const nullableText = (value: unknown) => (value === null ? null : text(value))

function summary(value: unknown): GuideLessonSummary {
  const item = object(value)

  if (typeof item.completed !== "boolean") return invalid()

  return {
    key: key(item.key),
    position: integer(item.position, 1),
    topic_key: key(item.topic_key),
    topic_title: text(item.topic_title),
    short_label: text(item.short_label),
    title: text(item.title),
    summary: optionalText(item.summary),
    content_version: integer(item.content_version, 1),
    prerequisite_lesson_keys: array(item.prerequisite_lesson_keys, key),
    search_terms: array(item.search_terms, text),
    completed: item.completed,
    completed_at: nullableText(item.completed_at),
  }
}

export function parseGuideCourse(value: unknown): GuideCourse {
  const item = object(value)
  const lessons = array(item.lessons, summary)
  const total = integer(item.total_count)
  const completed = integer(item.completed_count)
  const suggested = item.suggested_lesson_key === null ? null : key(item.suggested_lesson_key)

  if (
    item.course_key !== "kehrblatt-guide" ||
    total !== lessons.length ||
    completed !== lessons.filter((lesson) => lesson.completed).length ||
    new Set(lessons.map((lesson) => lesson.key)).size !== total ||
    (suggested !== null && !lessons.some((lesson) => lesson.key === suggested))
  )
    return invalid()

  return {
    course_key: "kehrblatt-guide",
    title: text(item.title),
    description: text(item.description),
    total_count: total,
    completed_count: completed,
    suggested_lesson_key: suggested,
    lessons,
  }
}

function marker(value: unknown): GuideImageMarker {
  const item = object(value)
  const coordinates = ["x", "y", "width", "height"] as const
  for (const coordinate of coordinates) {
    const number = item[coordinate]

    if (typeof number !== "number" || !Number.isFinite(number) || number < 0 || number > 100)
      return invalid()
  }
  const result = {
    number: integer(item.number, 1),
    x: item.x as number,
    y: item.y as number,
    width: item.width as number,
    height: item.height as number,
  }

  if (
    result.x + result.width > 100 ||
    result.y + result.height > 100 ||
    result.width <= 0 ||
    result.height <= 0
  )
    return invalid()

  return result
}

function image(value: unknown): GuideImage {
  const item = object(value)
  const src = text(item.src)
  // Content files allow camelCase; the public Python response uses snake_case.
  const focusRegion = item.focus_region ?? item.focusRegion

  // Only bundled tutorial/help images; never remote tracking URLs or arbitrary files.
  if (
    !/^\/media\/(?:learning\/kehrblatt-guide|help)\/[^?#\\]+\.(?:png|jpe?g|webp)$/i.test(src) ||
    src.split("/").some((part) => part === "." || part === "..") ||
    src.includes("%")
  )
    return invalid()

  return {
    src,
    alt: text(item.alt),
    caption: text(item.caption),
    ...(item.markers === undefined ? {} : { markers: array(item.markers, marker) }),
    ...(focusRegion === undefined || focusRegion === null
      ? {}
      : {
          focusRegion: (() => {
            const { number, ...region } = marker({ ...object(focusRegion), number: 1 })

            return region
          })(),
        }),
  }
}

function uniqueKeys(values: { key: string }[]): void {
  if (new Set(values.map((value) => value.key)).size !== values.length) invalid()
}

function choice(value: unknown): GuideChoice {
  const item = object(value)
  const options = array(item.options, (value) => {
    const option = object(value)

    return {
      key: key(option.key),
      label: text(option.label),
      description: text(option.description),
    }
  })
  uniqueKeys(options)

  if (options.length < 2 || options.length > 12) return invalid()

  return { key: key(item.key), title: text(item.title), options }
}

function basicBlock(value: unknown): GuideBasicBlock {
  const item = object(value)
  const title = text(item.title)
  switch (item.kind) {
    case "instruction":
    case "disclosure":
      return { kind: item.kind, title, text: text(item.text), images: array(item.images, image) }
    case "notice":
      if (item.tone !== "info" && item.tone !== "warning") return invalid()

      return { kind: item.kind, title, tone: item.tone, text: text(item.text) }
    case "comparison":
    case "sequence":
      return {
        kind: item.kind,
        title,
        items: array(item.items, (value) => {
          const entry = object(value)

          return { title: text(entry.title), text: text(entry.text) }
        }),
      }
    case "checklist":
      return { kind: item.kind, title, items: array(item.items, text) }
    default:
      return invalid()
  }
}

function block(value: unknown, choices: GuideChoice[]): GuideBlock {
  const item = object(value)

  if (item.kind !== "choice" && item.kind !== "branch") return basicBlock(value)

  const choiceKey = key(item.choice_key)
  const referenced = choices.find((choice) => choice.key === choiceKey)

  if (!referenced) return invalid()

  if (item.kind === "choice") return { kind: item.kind, choice_key: choiceKey }

  const cases = array(item.cases, (value) => {
    const branch = object(value)
    const optionKey = key(branch.option_key)

    if (!referenced.options.some((option) => option.key === optionKey)) return invalid()

    return { option_key: optionKey, blocks: array(branch.blocks, basicBlock) }
  })

  if (new Set(cases.map((entry) => entry.option_key)).size !== cases.length) return invalid()

  return { kind: "branch", choice_key: choiceKey, cases }
}

function entryPoint(value: unknown): GuideEntryPoint | null {
  if (value === undefined || value === null) return null

  const entry = object(value)
  const targets = [
    "overview",
    "work-orders",
    "calendar",
    "notifications",
    "district",
    "properties",
    "customers",
    "users",
    "organization",
    "billing",
  ]

  if (
    !targets.includes(text(entry.target)) ||
    Object.keys(entry).some((field) => !["target", "label", "instruction"].includes(field)) ||
    !text(entry.label).trim() ||
    text(entry.label).length > 120 ||
    !text(entry.instruction).trim() ||
    text(entry.instruction).length > 1000
  )
    return invalid()

  return {
    target: entry.target as GuideEntryPoint["target"],
    label: text(entry.label),
    instruction: text(entry.instruction),
  }
}

export function parseGuideLesson(value: unknown): GuideLesson {
  const item = object(value)
  const choices = array(item.choices ?? [], choice)
  uniqueKeys(choices)
  const stages: GuideStage[] = array(item.stages ?? [], (value) => {
    const stage = object(value)

    return {
      key: key(stage.key),
      legacy_keys: array(stage.legacy_keys === undefined ? [] : stage.legacy_keys, key),
      title: text(stage.title),
      summary: optionalText(stage.summary),
      success_text: optionalText(stage.success_text),
      entry_point: entryPoint(stage.entry_point),
      blocks: array(stage.blocks, (value) => block(value, choices)),
    }
  })
  uniqueKeys(stages)
  const stageKeys = stages.flatMap((stage) => [stage.key, ...stage.legacy_keys])

  if (new Set(stageKeys).size !== stageKeys.length) return invalid()

  return {
    lesson: summary(item.lesson),
    introduction: text(item.introduction),
    entry_path: array(item.entry_path, text),
    entry_point: entryPoint(item.entry_point),
    choices,
    stages,
    steps: array(item.steps, (value) => {
      const step = object(value)

      return { title: text(step.title), text: text(step.text), images: array(step.images, image) }
    }),
    success_text: optionalText(item.success_text),
    troubleshooting: array(item.troubleshooting, (value) => {
      const help = object(value)

      return { title: text(help.title), text: text(help.text) }
    }),
    related_lesson_keys: array(item.related_lesson_keys, key),
  }
}
